'use client';

import { Popover, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { BellIcon } from './ui/Icons';
import Link from 'next/link';
import Notifications from './business/Notifications';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export default function NotificationsPopover(props: any) {
  const {
    notificationsData,
    isValidating,
    viewLink,
    nfcKey,
    crFrom,
    popoverClass = '',
    mutateKey,
  } = props;
  const [isVisible, setIsVisible] = React.useState(false);

  const t = useTranslations();
  const NOTIFICATIONS = `res_panel.notifications`;

  React.useEffect(() => {
    const lastNotification = localStorage.getItem(nfcKey);
    let lastTimestamp = -1;
    let total = 0;
    if (notificationsData?.data?.length) {
      lastTimestamp = new Date(
        notificationsData?.data?.[0]?.created_at
      ).getTime();
      total = notificationsData?.meta?.total;
    }
    if (!lastNotification && notificationsData?.data?.length) {
      setIsVisible(true);
    } else if (lastNotification) {
      const localValue = JSON.parse(lastNotification);
      if (total > localValue?.total || lastTimestamp > localValue.timestamp) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }
  }, [notificationsData]);

  const handleShow = (isOpen: boolean) => {
    if (!isOpen) return;

    const nfcLocal = localStorage.getItem(nfcKey);
    let lastTimestamp = -1;
    let total = 0;
    if (notificationsData?.data?.length) {
      lastTimestamp = new Date(
        notificationsData?.data?.[0]?.created_at
      ).getTime();
      total = notificationsData?.meta?.total;
    }
    let updatedValues = {
      isViewed: true,
      timestamp: lastTimestamp,
      total,
    };
    if (nfcLocal) {
      const localValue = JSON.parse(nfcLocal);
      if (lastTimestamp > localValue?.timestamp) {
        updatedValues = {
          isViewed: true,
          timestamp: lastTimestamp,
          total,
        };
      }
    }
    const stringifiedValue = JSON.stringify(updatedValues);
    localStorage.setItem(nfcKey, stringifiedValue);
    setIsVisible(false);
  };

  return (
    <>
      <Popover className={cn('relative', popoverClass)}>
        {({ open, close }) => (
          <>
            <Popover.Button
              onClick={() => handleShow(!open)}
              className={`
                ${open ? '' : 'text-opacity-90'}
                group inline-flex items-center rounded-md px-3 py-2 text-base font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75`}
              aria-label={t(`${NOTIFICATIONS}.view_notifications`)}
            >
              <BellIcon
                className={`h-6 w-6 ${
                  isVisible ? 'text-primary animate-rotate' : ''
                } `}
                aria-hidden="true"
              />
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 z-10 mt-3 w-[250px] max-w-sm sm:w-[380px] sm:px-0 lg:max-w-3xl">
                <div className="space-y-5 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white py-8 shadow-lg">
                  <div className="flex items-center justify-between px-8">
                    <h3 className="text-h6 font-medium text-[#414B58]">
                      {t(`${NOTIFICATIONS}.header`)}
                    </h3>
                    <span>
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21.75 7.25586V17.7559C21.75 18.3526 21.5129 18.9249 21.091 19.3468C20.669 19.7688 20.0967 20.0059 19.5 20.0059H4.5C3.90326 20.0059 3.33097 19.7688 2.90901 19.3468C2.48705 18.9249 2.25 18.3526 2.25 17.7559V7.25586M21.75 7.25586C21.75 6.65912 21.5129 6.08683 21.091 5.66487C20.669 5.24291 20.0967 5.00586 19.5 5.00586H4.5C3.90326 5.00586 3.33097 5.24291 2.90901 5.66487C2.48705 6.08683 2.25 6.65912 2.25 7.25586M21.75 7.25586V7.49886C21.75 7.88301 21.6517 8.26076 21.4644 8.59616C21.2771 8.93155 21.0071 9.2134 20.68 9.41486L13.18 14.0299C12.8252 14.2484 12.4167 14.3641 12 14.3641C11.5833 14.3641 11.1748 14.2484 10.82 14.0299L3.32 9.41586C2.99292 9.2144 2.72287 8.93255 2.53557 8.59716C2.34827 8.26177 2.24996 7.88401 2.25 7.49986V7.25586"
                          stroke="#9099A5"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <div className="">
                    <Notifications
                      crFrom="nfc-popover"
                      nfcFor={crFrom}
                      nfcClass="px-8 py-2"
                      nfcListClass="mt-4 divide-y divide-gray-200 max-h-[400px] overflow-y-auto"
                      notificationsData={notificationsData}
                      isValidating={isValidating}
                      viewLink={viewLink}
                      handleClick={() => close()}
                      mutateKey={mutateKey}
                    />
                  </div>
                  {notificationsData?.meta?.last_page > 1 && (
                    <div className=" px-8 pt-5 text-center">
                      <Link
                        href={viewLink}
                        className="text-sm font-medium text-title underline"
                      >
                        {t(`${NOTIFICATIONS}.view_notifications`)}
                      </Link>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </>
  );
}
